import { useTranslation } from 'react-i18next';
import { ProgressRing } from '@/components/ui/ProgressRing';
import { CardSkeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';
import type { HealthScore } from '@/lib/insightsApi';

interface Props {
  data?: HealthScore;
  isLoading?: boolean;
}

const GRADE_COLOR: Record<string, string> = {
  excellent: '#10B981',
  good: '#2E5BFF',
  fair: '#F5A524',
  poor: '#F97316',
  critical: '#EF4444',
};

const GRADE_BG: Record<string, string> = {
  excellent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  good: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  fair: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  poor: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export function HealthScoreWidget({ data, isLoading }: Props) {
  const { t } = useTranslation();

  if (isLoading) return <CardSkeleton />;
  if (!data) return null;

  const color = GRADE_COLOR[data.grade] ?? '#2E5BFF';
  const gradeBg = GRADE_BG[data.grade] ?? GRADE_BG.good;

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold dark:text-white">
          {t('insights.health_score.title')}
        </h3>
        <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full', gradeBg)}>
          {t(`insights.health_score.grade.${data.grade}`)}
        </span>
      </div>

      <div className="flex items-center gap-6 mb-4">
        <ProgressRing value={data.total} size={72} strokeWidth={6} color={color} />
        <div>
          <p className="text-3xl font-bold dark:text-white">{data.total}</p>
          <p className="text-xs text-slate-500">{t('insights.health_score.out_of_100')}</p>
        </div>
      </div>

      {/* Breakdown */}
      {Object.entries(data.breakdown).length > 0 && (
        <div className="space-y-2 mb-4">
          {Object.entries(data.breakdown).map(([key, val]) => (
            <div key={key}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600 dark:text-slate-400 capitalize">
                  {key.replace(/_/g, ' ')}
                </span>
                <span className="font-medium dark:text-white">
                  {val.score}/{val.max}
                </span>
              </div>
              <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.round((val.score / val.max) * 100)}%`,
                    backgroundColor: color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Risk factors */}
      {data.risk_factors.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1.5">
            {t('insights.health_score.risk_factors')}
          </p>
          <ul className="space-y-1">
            {data.risk_factors.map((rf, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-red-400 shrink-0" />
                {rf}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
